// src/components/quote/RoyaltyFlow.jsx
//
// "Where each sale goes": one sale's retail price on the left, fanning out into a band per
// slice (print cost, ReVisionari, the author, each collaborator, anything unassigned).
//
// Hand-built SVG, no chart library. This is a single fan-out, not a general Sankey, so
// there is no node ordering or crossing to solve. And the prototype gives every band a
// minimum height so a 2% share stays readable, which a Sankey library's value-
// proportional bands would fight. The geometry is utils/royalty.js `flowLayout`; this
// only measures the space and draws.
//
// Colours are theme.js `chart`; the labels are white on the dark card, never the series
// colour, so the band beside a label carries the identity and the text stays legible.

import { useLayoutEffect, useRef, useState } from 'react';
import { tokens } from '../../theme';
import { FORMAT_NAMES, flowLayout, money } from '../../utils/royalty';

export default function RoyaltyFlow({ breakdown }) {
  const hostRef = useRef(null);
  const [width, setWidth] = useState(0);

  // Measured, not assumed: the card's width depends on the page's breakpoints.
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.round(el.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const L = flowLayout(breakdown, { width, colors: tokens.chart });
  const { W, H, narrow, fs, srcW, srcH, srcY, edgeW, nodeX, bandEnd, nodes } = L;
  const name = FORMAT_NAMES[breakdown.format];

  // The picture as a sentence, for screen readers.
  const description = `Where a ${money(breakdown.retail)} ${name.toLowerCase()} sale goes: ${
    nodes.map((n) => `${n.label} ${money(n.shown)}`).join(', ') || 'nothing yet'
  }.`;

  return (
    <div className="flow-host" ref={hostRef}>
      <svg className="flow-svg" viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={description}>
        <rect x="0" y={srcY} width={srcW} height={srcH} rx="3" fill={tokens.color.purpleSoft} fillOpacity="0.42" />
        <rect x={srcW} y={srcY} width={edgeW} height={srcH} rx="2" fill={tokens.color.purpleSoft} />
        <text x={srcW / 2} y={H / 2 - 4} textAnchor="middle" fontSize={narrow ? fs - 1 : fs} opacity=".85">
          {name}{narrow ? '' : ' retail'}
        </text>
        <text x={srcW / 2} y={H / 2 + fs + 6} textAnchor="middle" fontSize={fs + 5} fontWeight="600">
          {money(breakdown.retail)}
        </text>

        {nodes.map((n) => (
          <g key={n.key}>
            <path className="flow-ribbon" d={n.ribbon} fill={n.color} fillOpacity="0.32" />
            <rect x={nodeX} y={n.y} width="7" height={n.h} rx="1.5" fill={n.color} />
            <rect x={nodeX + 7} y={n.y} width={bandEnd - nodeX - 13} height={n.h} fill={n.color} fillOpacity="0.24" />
            <rect x={bandEnd - 6} y={n.y} width="6" height={n.h} rx="1.5" fill={n.color} />
            {narrow ? (
              <>
                <text x={nodeX + 14} y={n.cy - 3} fontSize={fs}>{n.label}</text>
                <text x={nodeX + 14} y={n.cy + fs + 1} fontSize={fs} fontWeight="600">
                  {money(n.shown)}
                  {n.sub && <tspan fillOpacity=".6" fontWeight="400" fontSize={fs - 1.5}>{` · ${n.sub}`}</tspan>}
                </text>
              </>
            ) : (
              <>
                <text x={nodeX + 18} y={n.twoLine ? n.cy - 3 : n.cy + fs * 0.36} fontSize={fs}>
                  {n.label}
                  {n.sub && !n.twoLine && <tspan fillOpacity=".6" fontSize={fs - 2}>{` · ${n.sub}`}</tspan>}
                </text>
                {n.twoLine && (
                  <text x={nodeX + 18} y={n.cy + fs} fillOpacity=".6" fontSize={fs - 2}>{n.sub}</text>
                )}
                <text x={bandEnd - 16} y={n.cy + fs * 0.36} textAnchor="end" fontSize={fs} fontWeight="600">
                  {money(n.shown)}
                </text>
              </>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}
